import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { DCSQueryClient } from "../../../../queryClient";
import { SectionHeader } from "../../../../components/SectionHeader/SectionHeader";
import { useToast } from "../../../../Providers/ToastProvider";
import { requests } from "../../../../services/http-common";
import { ContractForm } from "../ContractForm/ContractForm";
import { TContractForm } from "../Contracts.model";
import {
  buildSignedDocumentFormData,
  multipartRequestConfig,
} from "../signed-document";

export const ContractCreate = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const { show } = useToast();

  const createContract = useMutation({
    mutationFn: async ({
      newContract,
      preSigned,
    }: {
      newContract: TContractForm;
      preSigned?: { file: File; signedDate: Date };
    }) => {
      const created: any = await requests.post("/contract", {
        ...newContract,
        leadId,
      });

      if (preSigned) {
        if (created?.id) {
          try {
            await requests.post(
              `/contract/${created.id}/signed-document`,
              buildSignedDocumentFormData(
                [preSigned.file],
                preSigned.signedDate,
                "signed"
              ),
              multipartRequestConfig
            );
          } catch (uploadError) {
            show({
              severity: "warn",
              detail:
                "Contract created, but the signed document upload failed. Attach it from the contracts list.",
            });
          }
        } else {
          show({
            severity: "error",
            detail:
              "Contract was created, but the signed document could not be attached. Attach it from the contracts list.",
          });
        }
      }

      return created;
    },
    onSuccess: () => {
      show({ detail: "Contract was created successfully !" });
      navigate("/sales/contract");
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const saveDraft = useMutation({
    mutationFn: (draft: Partial<TContractForm>) => {
      return requests.post("/contract/draft", { ...draft, leadId });
    },
    onSuccess: () => {
      show({ detail: "Contract draft saved successfully!" });
      navigate("/sales/contract");
      DCSQueryClient.invalidateQueries({ queryKey: ["contract-list"] });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  if (!leadId) return null;

  return (
    <>
      <SectionHeader title="Create Contract" />
      <ContractForm
        onSubmit={(values, preSigned) =>
          createContract.mutate({ newContract: values, preSigned })
        }
        onSaveDraft={saveDraft.mutate}
        isPending={createContract.isLoading}
        isDraftPending={saveDraft.isLoading}
        leadId={leadId}
      />
    </>
  );
};
